import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { hasAdminRole } from './admin-role';

export interface Admin {
    id: number;
    email: string;
    name: string | null;
    role: string;
    status: string;
}

interface LoginResponse {
    accessToken: string;
    admin: Admin;
}

interface RefreshResponse {
    accessToken: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private http = inject(HttpClient);
    private router = inject(Router);

    readonly accessToken = signal<string | null>(null);
    readonly currentAdmin = signal<Admin | null>(null);
    readonly isAuthenticated = computed(() => this.currentAdmin() !== null);
    readonly isAdmin = computed(() => hasAdminRole(this.currentAdmin()));

    async login(email: string, password: string): Promise<void> {
        const res = await firstValueFrom(
            this.http.post<LoginResponse>(
                `${environment.baseUrl}/auth/login`,
                { email, password },
                { withCredentials: true }
            )
        );
        this.accessToken.set(res.accessToken);
        this.currentAdmin.set(res.admin);
    }

    async logout(): Promise<void> {
        try {
            await firstValueFrom(
                this.http.post(`${environment.baseUrl}/auth/logout`, {}, { withCredentials: true })
            );
        } catch {
            // logout es best-effort; si falla la red, igual limpiamos estado local
        }
        this.clearLocalSession();
        this.router.navigate(['/auth/login']);
    }

    async refresh(): Promise<boolean> {
        try {
            const res = await firstValueFrom(
                this.http.post<RefreshResponse>(
                    `${environment.baseUrl}/auth/refresh`,
                    {},
                    { withCredentials: true }
                )
            );
            this.accessToken.set(res.accessToken);
            await this.fetchProfile();
            return this.isAuthenticated();
        } catch {
            this.clearLocalSession();
            return false;
        }
    }

    async fetchProfile(): Promise<void> {
        if (!this.accessToken()) return;
        try {
            const profile = await firstValueFrom(
                this.http.get<Admin>(`${environment.baseUrl}/auth/me`)
            );
            this.currentAdmin.set(profile);
        } catch {
            this.clearLocalSession();
        }
    }

    private clearLocalSession(): void {
        this.accessToken.set(null);
        this.currentAdmin.set(null);
    }
}
